import { useId, useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  Field,
  LoadingState,
  Select,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  DOCTOR_LIST_PERMISSIONS,
  chronicApi,
  useBranchDoctorList,
  useCarePlan,
  useChronicAction,
  type CarePlan,
  type Due,
  type PatientRef,
  type RegisterRow,
} from '@/features/chronic/api';
import { recallsOf, remindersOn } from '@/features/chronic/recalls';
import { clinicDayLabel } from '@/pages/ops/mc-format';
import { FormDialog } from './FormDialog';
import parity from '@/pages/ops/parity.module.css';
import styles from './CarePlanDialog.module.css';

type Plan = NonNullable<CarePlan['plan']>;

const CONDITIONS_HINT =
  'Names the register conditions (diabetes, hypertension, asthma) on the care plan in the patient app, and also controls the Conditions list of the patient’s health record in the app. Off by default. Diagnoses are never shown.';

/**
 * Care plan drawer (RL-731, RL-1140): goals, next steps, who agreed the plan
 * (owner doctor), what the patient app shows and the automatic recall
 * reminders (RL-996). Opened from a register row, whose due checks are the
 * recalls the reminder job watches.
 */
export function CarePlanDialog({
  patient,
  row,
  canManage,
  onClose,
}: {
  patient: PatientRef;
  row?: RegisterRow;
  canManage: boolean;
  onClose: () => void;
}) {
  const q = useCarePlan(patient.id);
  const title = `Care plan · ${patient.fullName}`;
  if (q.isLoading || q.isError || !q.data)
    return (
      <Dialog
        open
        title={title}
        onClose={onClose}
        footer={<Button onClick={onClose}>Close</Button>}
      >
        {q.isError ? (
          <ErrorState
            error={q.error}
            title="Could not load the care plan"
            onRetry={() => void q.refetch()}
          />
        ) : (
          <LoadingState label="Loading the care plan" />
        )}
      </Dialog>
    );
  const plan = q.data.plan;
  if (!canManage)
    return (
      <Dialog
        open
        title={title}
        onClose={onClose}
        footer={<Button onClick={onClose}>Close</Button>}
      >
        <PlanSummary plan={plan} row={row} />
      </Dialog>
    );
  return (
    <CarePlanForm
      title={title}
      patientId={patient.id}
      plan={plan}
      row={row}
      onClose={onClose}
    />
  );
}

function PlanSummary({ plan, row }: { plan: Plan | null; row?: RegisterRow }) {
  if (!plan)
    return (
      <div className={parity.form}>
        <p>No care plan yet.</p>
        <Recalls row={row} on={remindersOn(null)} />
      </div>
    );
  return (
    <div className={parity.form}>
      <dl className={parity.kv}>
        <dt>Goals</dt>
        <dd className={styles.lines}>{plan.goals}</dd>
        <dt>Next steps</dt>
        <dd className={styles.lines}>{plan.nextSteps || '—'}</dd>
        <dt>Agreed with</dt>
        <dd>{plan.ownerDoctor?.fullName ?? 'No owner doctor set'}</dd>
        <dt>Patient app</dt>
        <dd>
          {plan.showInApp ? 'Plan shown' : 'Plan hidden'} · conditions{' '}
          {plan.showConditions ? 'named' : 'not named'}
        </dd>
      </dl>
      <Recalls row={row} on={remindersOn(plan)} />
      <UpdatedLine plan={plan} />
    </div>
  );
}

function UpdatedLine({ plan }: { plan: Plan }) {
  return (
    <span className={parity.sub}>
      Last updated {clinicDayLabel(plan.updatedAt)}
      {plan.updatedBy ? ` by ${plan.updatedBy}` : ''}
    </span>
  );
}

function CarePlanForm({
  title,
  patientId,
  plan,
  row,
  onClose,
}: {
  title: string;
  patientId: string;
  plan: Plan | null;
  row?: RegisterRow;
  onClose: () => void;
}) {
  const { me, hasPermission } = useSession();
  const a = useChronicAction(chronicApi.carePlan);
  const canList = DOCTOR_LIST_PERMISSIONS.some((p) => hasPermission(p));
  const doctors = useBranchDoctorList(canList);
  const [v, setV] = useState({
    goals: plan?.goals ?? '',
    nextSteps: plan?.nextSteps ?? '',
    showInApp: plan?.showInApp ?? true,
    showConditions: plan?.showConditions ?? false,
    ownerDoctorId: plan?.ownerDoctor?.id ?? '',
  });
  const appHintId = useId();
  const conditionsHintId = useId();

  const listed = doctors.data ?? [];
  const extra: { id: string; fullName: string }[] = [];
  // Keep the current owner selectable even when not in the list.
  if (plan?.ownerDoctor && !listed.some((d) => d.id === plan.ownerDoctor!.id))
    extra.push(plan.ownerDoctor);
  // Without the branch list, the signed-in doctor can still take the plan.
  if (
    !canList &&
    me &&
    !extra.some((d) => d.id === me.id) &&
    !listed.some((d) => d.id === me.id)
  )
    extra.push({ id: me.id, fullName: `${me.fullName} (me)` });
  const ownerOptions = [
    { value: '', label: 'No owner doctor' },
    ...[...listed, ...extra].map((d) => ({ value: d.id, label: d.fullName })),
  ];
  const ownerHint = !canList
    ? 'Your role cannot list the branch’s doctors, so only you or the current owner can be chosen here.'
    : doctors.isError
      ? 'Could not load the branch’s doctors.'
      : 'Shown in the patient app as “Agreed with”. Out-of-range home readings are routed to this doctor. Without one, the doctor who last saved the plan is used.';

  return (
    <FormDialog
      title={title}
      onClose={onClose}
      pending={a.isPending}
      disabled={v.goals.trim().length < 2}
      submitLabel="Save plan"
      onSubmit={() =>
        a.mutateAsync({
          patientId,
          goals: v.goals,
          nextSteps: v.nextSteps,
          showInApp: v.showInApp,
          showConditions: v.showConditions,
          ownerDoctorId: v.ownerDoctorId || null,
        })
      }
    >
      <Field label="Goals (one per line)">
        <textarea
          className={parity.textarea}
          rows={4}
          value={v.goals}
          maxLength={2000}
          onChange={(e) => setV({ ...v, goals: e.target.value })}
        />
      </Field>
      <Field label="Next steps (one per line)">
        <textarea
          className={parity.textarea}
          rows={4}
          value={v.nextSteps}
          maxLength={2000}
          onChange={(e) => setV({ ...v, nextSteps: e.target.value })}
        />
      </Field>
      <Field label="Owner doctor (agreed with)" hint={ownerHint}>
        <Select
          value={v.ownerDoctorId}
          options={ownerOptions}
          disabled={canList && doctors.isLoading}
          onChange={(e) => setV({ ...v, ownerDoctorId: e.target.value })}
        />
      </Field>
      <fieldset className={styles.group}>
        <legend className="eyebrow">Patient app</legend>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={v.showInApp}
            aria-describedby={appHintId}
            onChange={(e) => setV({ ...v, showInApp: e.target.checked })}
          />
          Show the plan in the patient app
        </label>
        <p id={appHintId} className={styles.hint}>
          Off hides the plan, its targets and its recalls in the app.
        </p>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={v.showConditions}
            aria-describedby={conditionsHintId}
            onChange={(e) => setV({ ...v, showConditions: e.target.checked })}
          />
          Show conditions in the patient app
        </label>
        <p id={conditionsHintId} className={styles.hint}>
          {CONDITIONS_HINT}
          {!v.showInApp &&
            ' No effect while the plan is hidden from the app: the record’s conditions list stays hidden too.'}
        </p>
      </fieldset>
      <Recalls
        row={row}
        on={remindersOn(plan ? { showInApp: v.showInApp } : null)}
        pendingSave={!!plan && v.showInApp !== plan.showInApp}
      />
      {plan && <UpdatedLine plan={plan} />}
    </FormDialog>
  );
}

/** What the RL-996 reminder job does for this patient; read only (no API setting). */
function Recalls({
  row,
  on,
  pendingSave = false,
}: {
  row?: RegisterRow;
  on: boolean;
  pendingSave?: boolean;
}) {
  const lines = row ? recallsOf(row) : [];
  return (
    <section className={styles.recalls} aria-label="Automatic recall reminders">
      <h3 className={styles.heading}>
        Automatic recall reminders{' '}
        <Badge tone={on ? 'success' : 'neutral'}>
          {on ? 'On' : 'Off'}
          {pendingSave ? ' when saved' : ''}
        </Badge>
      </h3>
      <p className={styles.hint}>
        {on
          ? 'The patient app gets one reminder when a check below falls due (09:00 to 20:00, branch time). Checks more than 14 days overdue are not sent. Patients without the app, without notification consent or with visit reminders turned off are skipped.'
          : 'Off while the plan is hidden from the patient app. Turn “Show the plan in the patient app” on to send them.'}
      </p>
      {lines.length > 0 && (
        <ul className={styles.recallList}>
          {lines.map((l) => (
            <li key={l.key}>
              <span>{l.title}</span>
              <DueText due={l.due} />
            </li>
          ))}
        </ul>
      )}
      <p className={styles.hint}>
        Intervals follow the clinic’s chronic care settings.
      </p>
    </section>
  );
}

function DueText({ due }: { due: Due }) {
  if (!due.dueAt) return <Badge tone="danger">Never done</Badge>;
  return due.overdue ? (
    <Badge tone="danger">Overdue since {clinicDayLabel(due.dueAt)}</Badge>
  ) : (
    <span>Due {clinicDayLabel(due.dueAt)}</span>
  );
}
